import { omit } from 'lodash-es';
import { analysisSortKey, generateSortKey, isEmpty } from '@intelligence/v3/utils/common';
import { RATIO, getStdAUMetricKey, generateMetricKey } from '@intelligence/v3/utils/metric';
import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';
import { SortType } from '@home/enums/common';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import commonSetting from '@intelligence/components/DownloadCommon/DownloadPage/configs/commonSetting';
import { convertParams } from '@intelligence/v3/pages/TopCharts/MobileGames/_services/utils';

const groupByGenerators = [
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'region',
    condition: (payload: string) => payload === 'region',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'market',
    condition: (payload: string) => payload === 'market',
  },
  {
    formModelKey: 'downlaod_date_method',
    groupByKey: 'date',
    condition: (payload: string) => payload !== 'aggregated',
  },
];

const getFileName = (params: any) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const newStyleDownloadFileName = params?.new_style_donwload_file_name || '';
  if (!!startDate && !!endDate) {
    return `${newStyleDownloadFileName}_${startDate}_${endDate}`;
  }
  return `${newStyleDownloadFileName}`;
};

/**
 * 获取下载的markets入参
 * @param params 参数对象
 * @param _groupBys groupBy配置
 * @return 下载入参的国家列表
 */
const getDownloadMarkets = (params?: IRecord, _groupBys?: string[]): string[] => {
  let markets = params?.regions || [];

  if (isEmpty(markets) || markets.length === 0) {
    markets = ['global'];
  }

  // if (markets.includes('global') && (groupBys?.includes('region') || groupBys?.includes('market'))) {
  //   markets = getAllCountryCodesByRegions(params?.regionOptions);
  // }
  //
  // if (markets.includes('global_wo_china') && (groupBys?.includes('region') || groupBys?.includes('market'))) {
  //   markets = getAllCountryCodesByRegions(params?.regionOptions);
  //   markets = markets.filter((market: string) => market !== 'cn');
  // }

  return markets;
};

const getDownloadApiParams = (params: IRecord): IMobileTopChartsDataFetchParams => {
  if (!params) return {} as unknown as IMobileTopChartsDataFetchParams;

  const pageMetrics = params?.metrics;
  const fetchParams = convertParams(pageMetrics, params);

  const checkedGameIds = (() => {
    const downloadGameIds = params?.select_ids || [];
    if (!isEmpty(downloadGameIds)) {
      return downloadGameIds;
    }

    const pageGameIds = fetchParams?.game_ids || [];
    if (!isEmpty(pageGameIds)) {
      const selectTop = params?.[DOWNLOAD_CFG_KEY.SELECT_TOP];
      const isSelectTopValid = typeof selectTop === 'number' && !Number.isNaN(selectTop) && selectTop > 0;
      return !isSelectTopValid ? pageGameIds : pageGameIds.slice(0, selectTop);
    }

    return [];
  })();

  const metrics = (() => {
    if (isEmpty(pageMetrics)) return [];
    const downloadDataSources = params?.data_sources || [];
    const downloadMetrics = params?.download_metrics || [];
    const downloadMetricKeys: string[] = [];

    for (const downloadDataSource of downloadDataSources) {
      for (const downloadMetric of downloadMetrics) {
        const downloadMetricStd = getStdAUMetricKey(downloadMetric);
        const downloadMetricKey = generateMetricKey({ dataSource: downloadDataSource, metric: downloadMetricStd });
        downloadMetricKeys.push(downloadMetricKey);
      }
    }

    if (downloadMetricKeys.length > 0) {
      return downloadMetricKeys;
    }

    return fetchParams?.metrics || [];
  })();

  const groupBys = (() => {
    const groupBys: string[] = [];

    groupByGenerators.forEach((generator) => {
      const formModelKey = generator?.formModelKey;
      const groupByKey = generator?.groupByKey;
      const condition = generator?.condition;
      if (!!condition?.(params?.[formModelKey])) {
        groupBys.push(groupByKey);
      }
    });

    if (!groupBys.includes('market')) return groupBys;

    const marketGroupByIndex = groupBys.indexOf('market');
    groupBys.splice(marketGroupByIndex, 0, 'region');
    return groupBys;
  })();

  const metricOrderBy = (() => {
    const pageMetricOrderBy = fetchParams?.order_by?.[0] || '';
    // @ts-ignore
    const { sortKey, sortType } = analysisSortKey(pageMetricOrderBy) || {};
    // @ts-ignore
    const metricKey = sortKey?.replace(new RegExp(`${RATIO}$`), '') || '';
    const metricKeyNew = metrics?.includes(metricKey) ? metricKey : metrics?.[0];
    return generateSortKey(metricKeyNew, sortType || SortType.Desc);
  })();

  const orderBys = (() => {
    if (groupBys.length === 0) return [metricOrderBy];

    const downloadGroupBys = groupBys.map((groupBy) => `${groupBy} desc`);
    return [...downloadGroupBys, metricOrderBy];
  })();

  const pageSize = checkedGameIds?.length > 0 ? checkedGameIds.length : params?.[DOWNLOAD_CFG_KEY.SELECT_TOP];

  const useCustomFields = [
    'publisher',
    'country_name_en',
    'parent_publisher',
    'developer',
    'developer_country_name_en',
    'developer_parent',
    'genre',
    'sub_genre',
    'iegg_genre',
    'iegg_sub_genre',
    'sensortower_genre',
    'sensortower_sub_genre',
    'super_genre',
    'major_category',
    'mid_category',
    'sub_category',
    'is_special_release_time',
    'release_time',
    'release_time_en',
    'release_time_cn',
  ];

  // Add game_engine to use_custom_fields when hasGameEngine is checked
  if (params?.game_engine_show === 1 || params?.game_engine_show === '1') {
    useCustomFields.push('game_engine', 'game_engine_show');
  }

  const paramsNew: any = {
    ...omit(fetchParams, ['backup_order_by', 'keyword']),
    game_ids: checkedGameIds,
    metrics,
    markets: getDownloadMarkets(params, groupBys),
    group_by: groupBys,
    order_by: orderBys,
    page_size: pageSize,
    use_custom_fields: useCustomFields,
  };

  return paramsNew;
};

export default (params: IRecord) => {
  return {
    ...commonSetting,
    downloadMode: 'commonConf',
    downloadConfigKey: 'mobileTopChartsMultiDataSource',
    downloadFetchApi: '/api/v1/intelligence_pc/mobile/metrics',
    downloadApiParams: getDownloadApiParams(params),
    isUsingNewMethod: false,
    fetchOnlyOnce: true,
    dataKey: undefined,
    $$fileName: getFileName(params),
    regionOptions: params?.regionOptions,
  };
};
